import * as moment from "moment";
import { CostAssessmentRecordRow, DepreciatedRecordRow } from "../../types";
import { RecordOptions } from "./calculation";
import { normalizeRecords } from "./normalize";
import { ModelAssumptionsUtil } from "src/app/core/v2/state/valuation-process/core/income/model-assumptions-util";

export interface StraightLineOptions {
  kind: 'straight-line',
  economicLife: number,
  salvageValue: number
}

export function straightLineMethod(
  records: CostAssessmentRecordRow[],
  recordOptions: RecordOptions,
  options: StraightLineOptions
) {
  const { economicLife, salvageValue } = options
  const { valuationDate, buildingConstructionDate, size } = recordOptions

  const _records = normalizeRecords(records, recordOptions)
  const _valuationDate = moment(valuationDate)
  const _buildingConstructionDate = moment(buildingConstructionDate)
  const yearDiff = ModelAssumptionsUtil._yearsDiff(
    _valuationDate,
    _buildingConstructionDate
  )
  if (isNaN(yearDiff)) return null;
  const _mappedRecords: DepreciatedRecordRow[] = _records.map(item => {
    return {
      ...item,
      ...depreciatedCost(item, economicLife, salvageValue, yearDiff, size)
    }
  })
  const _totalCost = _records.reduce(
    (acc, item) => {
      if (item.kind === 'header') return acc
      if (item.is_removed) return acc
      return acc + item.total_cost
    },
    0
  )
  const _totalDepreciatedCost = _mappedRecords.reduce(
    (acc, item) => {
      if (item.kind === 'header') return acc
      if (item.is_removed) return acc
      return acc + item.depreciated_cost
    },
    0
  )

  return {
    records: _mappedRecords,
    totalCost: _totalCost,
    graphValues: graphValues(_totalCost, economicLife, salvageValue),
    totalDepreciatedCost: _totalDepreciatedCost,
    yearDiff
  }
}

function depreciatedCost(
  {total_cost}: {total_cost: number},
  economicLife: number,
  salvageValue: number,
  yearDiff: number,
  size: number
): {depreciated_cost: number, depreciated_per_unit: number} {
  const costDiff = total_cost - salvageValue
  const depreciated_cost = total_cost - ((costDiff / economicLife) * yearDiff)
  return {
    depreciated_cost,
    depreciated_per_unit: depreciated_cost / size
  }
}

function graphValues(
  totalCost: number,
  economicLife: number,
  salvageValue: number
): {age: number, depreciatedValue: number}[] {
  const values = []
  for (let i = 0; i <= economicLife; i++) {
    const cumulative = ((totalCost - salvageValue) / economicLife) * i
    const depreciatedValue = totalCost - cumulative
    values.push({
      age: i,
      depreciatedValue,
    })
  }
  return values
}